import { Box, Button, Stack, Typography } from "@mui/material";
import { useState } from "react";
import Debt from "../../Components/Debt/Debt";
import DebtDetail from "../../Components/Debt/DebtDetail";
import CModal from "../../Components/CModal/CModal";
import GridContainer from "../../Components/GridContainer";
import PaymentsSelectionBar from "../../Components/PaymentsSelectionBar/PaymentsSelectionBar";
import BulkPayModal from "../../Components/BulkPayment/BulkPayModal";
import BulkSnoozeDateSelectionModal from "../../Components/TotalDebts/BulkSnoozeDateSelectionModal";
import DesktopTotalDebts from "../../Components/TotalDebts/DesktopTotalDebts";
import MobileTotalDebts from "../../Components/TotalDebts/MobileTotalDebts";
import { CreditStatus, DebtType } from "../../Models/Credit";

/**
 * CRL-3241: local component gallery with mock data for the /design-guide route.
 * Not linked anywhere in the app; used to review the redesigned components
 * without a logged-in session. Safe to delete.
 */

const shop = (name: string) => ({
  id: "1",
  name,
  logoUrl: "/debt-card-logo4x.png",
});

const mockDebt = (over: Partial<DebtType>): DebtType => ({
  id: 1,
  creditId: 24009918,
  status: CreditStatus.UNPAID,
  creditAmount: 312.4,
  paymentAmount: 312.4,
  remainingBaseAmount: 312.4,
  remainingPaymentAmount: 312.4,
  totalRemainingLateAmount: 0,
  baseInterestAmount: 0,
  totalAmount: 312.4,
  baseAmount: 312.4,
  snoozeAmount: 0,
  totalRemainingAmount: 312.4,
  serviceFee: 0,
  remainingServiceFee: 0,
  createdAt: "2026-06-02T00:00:00Z",
  dueDate: "2026-08-16T00:00:00Z",
  totalPaidAmount: 0,
  snoozeAvailable: true,
  maxSnoozeDuration: 30,
  shop: shop("Migros Yemek"),
  ...over,
});

const debts: DebtType[] = [
  mockDebt({
    id: 1,
    status: CreditStatus.LATE,
    dueDate: "2026-08-22T00:00:00Z",
    snoozeAvailable: false,
    shop: shop("Migros Hemen"),
  }),
  mockDebt({ id: 2, remainingPaymentAmount: 590, totalPaidAmount: 100 }),
  mockDebt({
    id: 3,
    remainingPaymentAmount: 168.39,
    dueDate: "2026-08-20T00:00:00Z",
    shop: shop("Migros Hemen"),
  }),
  mockDebt({
    id: 4,
    status: CreditStatus.PAID,
    totalAmount: 245.5,
    shop: shop("Usta Dönerci"),
  }),
  // Pending detail modal: snoozed before (snooze fee accrued), not snoozable again.
  mockDebt({
    id: 5,
    creditId: 24048367,
    creditAmount: 590,
    remainingPaymentAmount: 590,
    createdAt: "2026-06-17T00:00:00Z",
    dueDate: "2026-08-16T00:00:00Z",
    snoozeAvailable: false,
    snoozeAmount: 15,
    shop: shop("Migros Yemek"),
  }),
];

const DesignGuidePage = () => {
  const [selected, setSelected] = useState<number[]>([3]);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [bulkPayOpen, setBulkPayOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const toggle = (id: number) =>
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <Box className="md:p-[4%] lg:p-[6%] !py-8 px-4">
      <Typography fontSize={24} fontWeight={800} pb={2}>
        Design Guide (mock data)
      </Typography>

      <Typography fontWeight={700} pt={3}>Desktop total debts</Typography>
      <DesktopTotalDebts
        totalDebtsData={{
          totalAmount: 3545.93,
          totalLateAmount: 312.4,
          activeCreditCount: 11,
          lateCreditCount: 1,
          masterpassAutoRepaymentEnabled: false,
        }}
        snoozeAvailability={{
          isSnoozeAvailable: true,
          startDate: "2026-08-28",
          endDate: "2026-09-30",
        }}
        onBulkSnoozeClick={() => setSnoozeOpen(true)}
        onBulkPayClick={() => setBulkPayOpen(true)}
      />

      <Typography fontWeight={700} pt={4}>Mobile total debts</Typography>
      <Box maxWidth={390}>
        <MobileTotalDebts
          totalDebtsData={{
            totalAmount: 3545.93,
            totalLateAmount: 312.4,
            activeCreditCount: 11,
            lateCreditCount: 1,
            masterpassAutoRepaymentEnabled: false,
          }}
          snoozeAvailability={{
            isSnoozeAvailable: true,
            startDate: "2026-08-28",
            endDate: "2026-09-30",
          }}
          onBulkSnoozeClick={() => setSnoozeOpen(true)}
          onBulkPayClick={() => setBulkPayOpen(true)}
        />
      </Box>

      <Typography fontWeight={700} pt={4}>Payment cards</Typography>
      <GridContainer gridCount={3} theme={undefined}>
        {debts.map((d) => (
          <Debt
            key={d.id}
            debt={d}
            paymentPlans={debts}
            selectable={d.status !== CreditStatus.PAID}
            selected={selected.includes(d.id)}
            onToggleSelect={() => toggle(d.id)}
            detailOnPress
          />
        ))}
      </GridContainer>

      <Stack direction="row" gap={2} pt={2}>
        <Button variant="outlined" onClick={() => setSnoozeOpen(true)}>
          Bulk snooze modal
        </Button>
        <Button variant="outlined" onClick={() => setDetailOpen(true)}>
          Completed detail modal
        </Button>
        <Button variant="outlined" onClick={() => setBulkPayOpen(true)}>
          Bulk pay modal
        </Button>
      </Stack>

      <PaymentsSelectionBar
        count={selected.length}
        totalAmount={debts
          .filter((d) => selected.includes(d.id))
          .reduce((s, d) => s + d.remainingPaymentAmount, 0)}
        payEnabled={selected.length === 1 || selected.length === 3}
        onClear={() => setSelected([])}
        onPay={() => {}}
        snoozeEnabled={debts.some(
          (d) => selected.includes(d.id) && d.snoozeAvailable
        )}
        snoozePartial={debts.some(
          (d) => selected.includes(d.id) && !d.snoozeAvailable
        )}
        onSnooze={() => setSnoozeOpen(true)}
        items={debts.filter((d) => selected.includes(d.id))}
        onRemove={(d) => toggle(d.id)}
      />

      <BulkPayModal
        open={bulkPayOpen}
        onClose={() => setBulkPayOpen(false)}
        payments={debts.filter((d) => d.status !== CreditStatus.PAID)}
        totalAmount={3545.93}
        onConfirm={() => setBulkPayOpen(false)}
      />

      <BulkSnoozeDateSelectionModal
        open={snoozeOpen}
        onClosed={() => setSnoozeOpen(false)}
        onPressed={() => setSnoozeOpen(false)}
        startDate={new Date("2026-08-28")}
        endDate={new Date("2026-09-30")}
        paymentDates={debts.map((d) => d.dueDate)}
        payments={debts.filter((d) => d.status !== CreditStatus.PAID)}
      />

      {detailOpen && (
        <CModal
          open={detailOpen}
          onClose={() => setDetailOpen(false)}
          contentSx={{
            "@media (min-width: 768.5px)": {
              width: "min(480px, 92vw)",
              borderRadius: "24px",
            },
          }}
        >
          <DebtDetail
            debtDetail={{
              id: 4,
              creditId: 23288015,
              status: CreditStatus.PAID,
              creditAmount: 245.5,
              totalPaidAmount: 245.5,
              totalPaidLateAmount: 0,
              totalPaidServiceFee: 0,
              createdAt: "2026-01-20T00:00:00Z",
              dueDate: "2026-03-20T00:00:00Z",
            }}
            shop={shop("Usta Dönerci")}
          />
        </CModal>
      )}
    </Box>
  );
};

export default DesignGuidePage;
